import { Component, OnInit,effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';
import { SettingsService } from 'src/app/shared/services/settings';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import * as _ from 'lodash';

@Component({
    selector: 'app-project-datasets-settings-home',
    templateUrl: './project-datasets-settings-home.component.html',
    styleUrls: ['./project-datasets-settings-home.component.scss'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule]
})
export class ProjectDatasetsSettingsHomeComponent implements OnInit {
    showDatasetModal: boolean = false;
    isEditing: boolean = false;
    datasetForm: FormGroup;
    datasets: Array<{ name: string; file_name: string; created_date: string; file_size: string }> = [];
    dataClone: Array<{ name: string; file_name: string; created_date: string; file_size: string }> = [];
    tableData: Array<{ name: string; file_name: string; created_date: string; file_size: string }> = [];
    sortedData: Array<{ name: string; file_name: string; created_date: string; file_size: string }> = [];
    searchBoxVisible: boolean = true;
    searchText: string = '';
    isTextEntered: boolean = false;
    loading: boolean = false;
    selectedFile: File | null = null;
    selectedFileName: string = '';
    private searchSubject = new Subject<Event>();
    // Track dataset names for duplicate checks and upload state
    private datasetNames: string[] = [];
    isUploading: boolean = false;
    private lastDuplicateToastFor: string | null = null;
    hasExistingDatasets: boolean = false;
    private hasAnnouncedExistingState: boolean = false;
    datasetHeaders: string[] = [];
    brandDetails: any = {};
    private hasTriggeredInitialLoad: boolean = false;

    // Sorting properties
    sortColumn: string | null = null;
    sortDirection: 'asc' | 'desc' = 'asc';

    // Paging state
    pagingDetails: any = {
        start: 0,
        end: 0,
        pageSize: 10,
        pageNumber: 1,
        total: 0,
        totalpages: 1
    };
    pagingButtons: any = { isNext: false, isPrevious: false, isFirst: false, isLast: false };

    isUserHasWriteAccess: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private settingsService: SettingsService,
        private appStateService: AppStateService,
        private toastService: ToastService
    ) {
        effect(() => {
            if (this.appStateService.initialized()) {
                this.isUserHasWriteAccess = this.isUserHasWriteAccessFn();
                const brandDetails = this.appStateService.brandDetails() as any;
                this.brandDetails = brandDetails;
                if (!this.hasTriggeredInitialLoad && this.brandDetails && this.brandDetails._id) {
                    this.hasTriggeredInitialLoad = true;
                    this.loadDatasets();
                }
            }
        })
    
        // Initialize reactive form controls
        this.datasetForm = this.formBuilder.group({
            name: ['', [Validators.required, Validators.minLength(3)]],
            file: [null, Validators.required],
            displayField: [{ value: '', disabled: true }, Validators.required],
            valueField: [{ value: '', disabled: true }, Validators.required],
            parentValueField: [{ value: '', disabled: true }]
        });

        // Clone datasets to maintain a backup for resetting or filtering
        // this.dataClone = [...this.datasets];
    }

    /**
     * Lifecycle hook: Initializes component state.
     * - Subscribes to search input events with debounce.
     * - Fetches datasets from API based on brand details.
     */
    ngOnInit(): void {
        this.searchSubject.pipe(debounceTime(300)).subscribe((event: Event) => {
            this.handleSearch(event);
        });

        // Initial load is triggered via effect when brand details are ready

        // Validate dataset name on user input to prevent duplicates
        const nameCtrl = this.datasetForm.get('name');
        nameCtrl?.valueChanges.pipe(debounceTime(300)).subscribe((val: string) => {
            this.validateDatasetNameInput(val);
        });
    }

    isUserHasWriteAccessFn(): boolean {
        let isWriteAccess: boolean = false;
        const adminRulesDic = _.cloneDeep(Helpers?.adminRulesDic || {});
        if (adminRulesDic.MANAGE_DATASETS_CREATION && !_.isEmpty(adminRulesDic.MANAGE_DATASETS_CREATION) && adminRulesDic.MANAGE_DATASETS_CREATION.write) {
            isWriteAccess = true;
        }else {
            isWriteAccess = false;
        }
        return isWriteAccess;
    }

    /**
     * Fetch datasets for current brand and update local state and names cache
     */
    private loadDatasets(): void {
        // const brandDetails = this.appStateService.brandDetails() as any;
        const payload = { brand_id: this.brandDetails._id };

        if (payload.brand_id) {
            this.settingsService.getDatasetList(payload).subscribe({
                next: (response: any) => {
                    const code = response?.data?.code;
                    if (code === 0) {
                        this.datasets = response?.data?.datasets || [];
                        this.dataClone = [...this.datasets];
                        this.datasetNames = this.datasets.map(d => (d?.name || '').toLowerCase());
                        this.updatePagingDetails(this.dataClone.length, 1, this.dataClone);
                        this.hasExistingDatasets = this.datasets.length > 0;
                        if (!this.hasAnnouncedExistingState) {
                            this.hasAnnouncedExistingState = true;
                        }
                    } else {
                        this.datasets = [];
                        this.dataClone = [];
                        this.updatePagingDetails(0, 1, []);
                        this.hasExistingDatasets = false;
                    }
                },
                error: err => {
                    console.error('API error:', err);
                    this.toastService.showPopup('ERROR', 'Error', 'Failed to load datasets. Please try again.');
                }
            });
        } else {
        }
    }

    /**
     * Uploads selected dataset file to the server.
     * Builds FormData with configuration and sends API request.
     */
    uploadDataset(): void {
        if (!this.isUserHasWriteAccess) {
            Helpers.smallAlertBox('Access Denied', 'You do not have write permission to upload a dataset. Only read access is available.', 'warning', 4000);
            return;
        }
        if (!this.selectedFile || this.datasetForm.invalid || this.isUploading) return;

        const brandDetails = this.appStateService.brandDetails() as any;

        // Build config from form fields and normalize as per legacy behavior
        const rawLabel = (this.datasetForm.get('displayField')?.value || '').toString();
        const rawValue = (this.datasetForm.get('valueField')?.value || '').toString();
        const rawParent = (this.datasetForm.get('parentValueField')?.value || '').toString();
        const datasetName = (this.datasetForm.get('name')?.value || '').toString().trim();

        if (!datasetName) {
            this.toastService.showPopup('WARN', 'Validation', 'Please enter a dataset name.');
            return;
        }

        // Validate dataset name input and check for duplicates
        if (!this.validateDatasetNameInput(datasetName)) {
            return;
        }

        const config = {
            label_field: this.normalizeFieldName(rawLabel),
            value_field: this.normalizeFieldName(rawValue),
            parent_field: rawParent ? this.normalizeFieldName(rawParent) : null,
        } as any;

        const formData = new FormData();
        formData.append('data', JSON.stringify({ config, brand_id: brandDetails && brandDetails._id, dataset_name: datasetName }));
        formData.append('file_name', this.selectedFile);

        this.isUploading = true;
        this.settingsService.uploadDatasetValueSet(formData).subscribe({
            next: (res) => {
                this.toastService.showPopup('SUCCESS', 'Success', 'Dataset uploaded successfully!');
                this.dismissModal();
                this.loadDatasets();
                this.isUploading = false;
            },
            error: (err) => {
                console.error('Upload error', err);
                this.toastService.showPopup('ERROR', 'Error', 'Failed to upload dataset. Please try again.');
                this.isUploading = false;
            },
        });
    }

    /**
     * Validates dataset name input; returns true if valid.
     * Applies duplicate error and shows toast when name already exists.
     */
    private validateDatasetNameInput(name: string): boolean {
        const trimmed = (name || '').toString().trim();
        const nameCtrl = this.datasetForm.get('name');
        if (!trimmed) {
            // Leave required/length validation to built-in validators
            // Clear duplicate error if present
            if (nameCtrl?.errors && nameCtrl.errors['duplicate']) {
                const { duplicate, ...rest } = nameCtrl.errors;
                nameCtrl.setErrors(Object.keys(rest).length ? rest : null);
            }
            this.lastDuplicateToastFor = null;
            return false;
        }
        const isDuplicate = this.isDuplicateDatasetName(trimmed);
        if (isDuplicate) {
            nameCtrl?.setErrors({ ...(nameCtrl.errors || {}), duplicate: true });
            if (this.lastDuplicateToastFor !== trimmed) {
                this.toastService.showPopup('WARN', 'Warning', 'Dataset name already exists, please enter another name.');
                this.lastDuplicateToastFor = trimmed;
            }
            return false;
        }
        // Clear duplicate error if present
        if (nameCtrl?.errors && nameCtrl.errors['duplicate']) {
            const { duplicate, ...rest } = nameCtrl.errors;
            nameCtrl.setErrors(Object.keys(rest).length ? rest : null);
        }
        this.lastDuplicateToastFor = null;
        return true;
    }

    /**
     * Checks if dataset name already exists (case-insensitive)
     */
    private isDuplicateDatasetName(name: string): boolean {
        if (!name) return false;
        const lower = name.toLowerCase();
        return this.datasetNames.includes(lower);
    }

    /**
     * Normalizes field names: trim, replace spaces with underscores, lower-case
     */
    private normalizeFieldName(value: string): string {
        return value ? value.trim().replace(/\s+/g, '_').toLowerCase() : '';
    }

    /**
     * Opens the dataset modal for creating a new dataset.
     */
    openDatasetModal(): void {
       if (!this.isUserHasWriteAccess) {
        Helpers.smallAlertBox('Access Denied', 'You do not have write permission to create a new dataset. Only read access is available.', 'warning', 4000);
        return;
       }
        this.isEditing = false;
        this.showDatasetModal = true;
    }

    /**
     * Closes dataset modal and resets the form state.
     */
    dismissModal(): void {
        this.showDatasetModal = false;
        this.datasetForm.reset();
        this.datasetForm.get('displayField')?.disable();
        this.datasetForm.get('valueField')?.disable();
        this.datasetForm.get('parentValueField')?.disable();
        this.selectedFile = null;
        this.selectedFileName = '';
    }

    /**
     * Handles file input change event.
     * Enables dependent form fields when a valid file is selected.
     */
    onFileSelected(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const file = inputElement.files && inputElement.files.length > 0 ? inputElement.files[0] : null;

        this.selectedFile = file;
        this.selectedFileName = file ? file.name : '';
        this.datasetForm.patchValue({ file });

        if (file) {
            this.datasetForm.get('displayField')?.enable();
            this.datasetForm.get('valueField')?.enable();
            this.datasetForm.get('parentValueField')?.enable();
            // Parse headers from CSV files to populate selects
            this.parseFileHeaders(file);
        } else {
            this.datasetForm.get('displayField')?.disable();
            this.datasetForm.get('valueField')?.disable();
            this.datasetForm.get('parentValueField')?.disable();
            this.datasetHeaders = [];
        }
    }

    /**
     * Parses headers from uploaded file (CSV/Excel)
     */
    private parseFileHeaders(file: File): void {
        this.datasetHeaders = [];
        const fileName = (file.name || '').toLowerCase();
        
        if (fileName.endsWith('.csv')) {
            const reader = new FileReader();
            reader.onload = () => {
                try {
                    const text = (reader.result as string) || '';
                    const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
                    if (lines.length > 0) {
                        const firstLine = lines[0];
                        const headers = this.parseCsvLine(firstLine);
                        this.datasetHeaders = headers.filter(h => h && h.trim().length).map(h => h.trim());
                        
                        if (this.datasetHeaders.length === 0) {
                            this.toastService.showPopup('WARN', 'Warning', 'No headers detected in the CSV file.');
                        }
                    }
                } catch (error) {
                    console.error('Error parsing CSV headers:', error);
                    this.toastService.showPopup('ERROR', 'Error', 'Failed to parse CSV headers.');
                }
            };
            reader.onerror = () => {
                this.toastService.showPopup('ERROR', 'Error', 'Failed to read the selected file.');
            };
            reader.readAsText(file);
        } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
            // For Excel files, show a message that CSV is preferred for header detection
            this.toastService.showPopup('INFO', 'Info', 'For best results, please use CSV format. Excel header detection is limited.');
            // Set some default headers for Excel files
            this.datasetHeaders = ['ID', 'Display Name', 'Value', 'Description'];
        } else {
            this.toastService.showPopup('WARN', 'Warning', 'Unsupported file format. Please use CSV or Excel files.');
        }
    }

    /**
     * Parses a CSV line into fields, handling quoted values
     */
    private parseCsvLine(line: string): string[] {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    // Escaped quote
                    current += '"';
                    i++; // Skip the next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                // Field separator
                result.push(current);
                current = '';
            } else {
                current += char;
            }
        }
        
        // Add the last field
        result.push(current);
        
        return result;
    }

    /**
     * Triggers the hidden file input click event.
     * Used when clicking the "Upload" button manually.
     */
    triggerFileInput(): void {
        const fileInput = document.getElementById('fileUpload') as HTMLInputElement;
        if (fileInput) fileInput.click();
    }

    /**
     * Placeholder for implementing additional file upload logic later.
     */
    uploadFile(): void {}

    /**
     * Validates and saves dataset form.
     * Currently dismisses modal after validation.
     */
    saveDataset(): void {
        if (this.datasetForm.invalid) {
            this.datasetForm.markAllAsTouched();
            return;
        }
        this.uploadDataset();
    }

    /**
     * Handles dataset pagination logic.
     * Supports navigating between first, previous, next, and last pages.
     */
    paginateDatasets(direction: 'first' | 'previous' | 'next' | 'last'): void {
        if (direction === 'next') this.pagingDetails.pageNumber++;
        else if (direction === 'previous' && this.pagingDetails.pageNumber > 1) this.pagingDetails.pageNumber--;
        else if (direction === 'first') this.pagingDetails.pageNumber = 1;
        else if (direction === 'last') this.pagingDetails.pageNumber = Math.max(1, this.pagingDetails.totalpages || 1);

        const sourceData = this.getSourceData();
        this.updatePagingDetails(sourceData.length, this.pagingDetails.pageNumber, sourceData);
    }

    /**
     * Returns appropriate dataset array based on sorting or filtering state.
     */
    private getSourceData(): Array<{ name: string; file_name: string; created_date: string; file_size: string }> {
        if (this.sortColumn && this.sortedData.length > 0) return this.sortedData;
        return this.filteredDatasets.length ? this.filteredDatasets : this.dataClone;
    }

    /**
     * Updates pagination details and computes visible data for the current page.
     */
    updatePagingDetails(totalfeeds: number, pageNumber: number, filteredData: Array<{ name: string; file_name: string; created_date: string; file_size: string }>): void {
        if (totalfeeds === 0) {
            this.pagingDetails = {
                start: 0,
                end: 0,
                pageSize: this.pagingDetails.pageSize,
                pageNumber: 1,
                total: 0,
                totalpages: 0
            };
            this.pagingButtons = { isNext: false, isPrevious: false, isFirst: false, isLast: false };
            this.tableData = [];
            return;
        }

        const start = ((pageNumber - 1) * this.pagingDetails.pageSize) + 1;
        const end = Math.min(pageNumber * this.pagingDetails.pageSize, totalfeeds);
        const totalpages = Math.ceil(totalfeeds / this.pagingDetails.pageSize);

        this.pagingDetails = { start, end, pageSize: this.pagingDetails.pageSize, pageNumber, total: totalfeeds, totalpages };
        this.pagingButtons = { isPrevious: pageNumber > 1, isNext: pageNumber < totalpages, isFirst: pageNumber > 1, isLast: pageNumber < totalpages };

        this.tableData = filteredData.slice(start - 1, end);
    }

    /**
     * Returns datasets filtered based on search text.
     */
    get filteredDatasets(): Array<{ name: string; file_name: string; created_date: string; file_size: string }> {
        const query = this.searchText.trim();
        if (!query) return this.dataClone;
        const tokens = this.normalizeForSearch(query).split(/\s+/).filter(Boolean);
        if (!tokens.length) return this.dataClone;
        return this.dataClone.filter(d => this.datasetMatchesTokens(d, tokens));
    }

    /**
     * Handles user input in the search box and triggers filtering.
     */
    onSearchInput(event: Event) {
        const input = (event.target as HTMLInputElement).value;
        const trimmed = input.trim();
        this.searchText = trimmed;

        if (trimmed === '') {
            this.clearSearchBox();
            return;
        }

        this.handleSearch(event);
        this.searchSubject.next(event);
    }

    /**
     * Filters dataset based on search query and updates pagination.
     */
    private handleSearch(event: Event) {
        const filtered = this.filteredDatasets;
        this.pagingDetails.pageNumber = 1;
        this.sortColumn = null;
        this.sortedData = [];
        this.updatePagingDetails(filtered.length, this.pagingDetails.pageNumber, filtered);
        this.isTextEntered = (this.searchText || '').length > 0;
    }

    /**
     * Clears the search box, resets filters and sorting.
     */
    clearSearchBox(): void {
        this.searchText = '';
        this.isTextEntered = false;
        this.pagingDetails.pageNumber = 1;
        this.sortColumn = null;
        this.sortedData = [];
        this.updatePagingDetails(this.dataClone.length, this.pagingDetails.pageNumber, this.dataClone);
    }

    /**
     * Normalizes strings for case-insensitive, space-insensitive search
     */
    private normalizeForSearch(value: string): string {
        return (value || '').toString().toLowerCase();
    }

    /**
     * Returns true if any dataset field matches all query tokens
     */
    private datasetMatchesTokens(
        d: { name: string; file_name: string; created_date: string; file_size: string },
        tokens: string[]
    ): boolean {
        const name = this.normalizeForSearch(d.name);
        const fileName = this.normalizeForSearch(d.file_name);
        const created = this.normalizeForSearch(d.created_date);
        const size = this.normalizeForSearch(d.file_size);
        // Require all tokens to be found in any of the searchable fields
        return tokens.every(t => name.includes(t) || fileName.includes(t) || created.includes(t) || size.includes(t));
    }

    /**
     * Generates CSV content string from table data.
     */
    private generateCSVContent(): string {
        const headers = ['Dataset Name', 'File Name', 'Upload Date', 'Size'];
        const csvRows = [headers.join(',')];

        this.tableData.forEach(dataset => {
            const row = [
                `"${dataset.name}"`,
                `"${dataset.file_name}"`,
                `"${dataset.created_date}"`,
                `"${dataset.file_size}"`
            ];
            csvRows.push(row.join(','));
        });

        return csvRows.join('\n');
    }

    /**
     * Returns appropriate sorting icon path for a column.
     */
    getSortIcon(column: string): string {
        if (this.sortColumn === column) {
            return this.sortDirection === 'asc' ? 'assets/images/arrow-up.svg' : 'assets/images/arrow.svg';
        }
        return 'assets/images/arrow-up.svg';
    }

    /**
     * Handles click on sortable column header.
     * Toggles sorting direction and sorts data accordingly.
     */
    onSort(column: string) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.sortData();
    }

    /**
     * Sorts dataset table based on selected column and direction.
     */
    sortData() {
        if (this.sortColumn) {
            const sourceData = this.filteredDatasets;
            sourceData.sort((a, b) => {
                let valA: any = a[this.sortColumn as keyof typeof a];
                let valB: any = b[this.sortColumn as keyof typeof b];

                if (this.sortColumn === 'created_date') {
                    valA = new Date(valA).getTime();
                    valB = new Date(valB).getTime();
                } else if (this.sortColumn === 'name') {
                    valA = valA.toLowerCase();
                    valB = valB.toLowerCase();
                }

                if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
                if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
                return 0;
            });

            this.sortedData = [...sourceData];
            this.pagingDetails.pageNumber = 1;
            this.updatePagingDetails(this.sortedData.length, this.pagingDetails.pageNumber, this.sortedData);
        }
    }

    /**
     * Refreshes dataset list.
     * Resets search, sorting, and pagination to original dataset state.
     */
    refreshData(): void {
        this.loading = true;
        this.loadDatasets();
        setTimeout(() => {
            this.searchText = '';
            this.isTextEntered = false;
            this.sortColumn = null;
            this.sortedData = [];
            this.pagingDetails.pageNumber = 1;
            this.updatePagingDetails(this.dataClone.length, this.pagingDetails.pageNumber, this.dataClone);
            this.loading = false;
        }, 200);
    }
}
